import { getStroke } from 'perfect-freehand';
import rough from 'roughjs/bin/rough';
import { boundsOf, isResizableElement, TEXT_FONT_FAMILY } from './snap.js';
import { BOARD_PALETTE, connectionMeta } from './palette.js';
import { extractLayers } from './layers.js';
import {
  MEDIA_CARD_PAD,
  MEDIA_CARD_TITLE_H,
  mediaCardInnerRect,
} from './mediaCard.js';
import {
  endpointCenter,
  linkAnchorPoint,
  resolveEndpoint,
} from './linkAnchors.js';
import { containsMath } from './math.js';

const GRID_STEP = 20;

function formatFileSize(bytes) {
  const n = Number(bytes);
  if (!Number.isFinite(n) || n <= 0) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(n < 10 * 1024 ? 1 : 0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(n < 10 * 1024 * 1024 ? 1 : 0)} MB`;
}

function freedrawPath(points, strokeWidth = 2) {
  const stroke = getStroke(points, {
    size: strokeWidth * 4,
    thinning: 0.62,
    smoothing: 0.5,
    streamline: 0.5,
    simulatePressure: true,
  });
  if (!stroke.length) return null;
  const d = stroke.reduce(
    (acc, [x, y], i) => (i === 0 ? `M ${x} ${y}` : `${acc} L ${x} ${y}`),
    '',
  );
  return `${d} Z`;
}

/** 按 maxWidth 折行；maxLines 为 0 表示不限制行数 */
function wrapTextLines(ctx, text, maxWidth, maxLines = 0) {
  const raw = String(text || '').replace(/\r\n/g, '\n');
  const paragraphs = raw.split('\n');
  const lines = [];
  for (const paragraph of paragraphs) {
    const trimmed = paragraph.trim();
    if (!trimmed) {
      if (lines.length && lines[lines.length - 1] !== '') lines.push('');
      continue;
    }
    let line = '';
    for (const ch of trimmed) {
      const next = line + ch;
      if (line && ctx.measureText(next).width > maxWidth) {
        lines.push(line);
        line = ch;
        if (maxLines > 0 && lines.length >= maxLines) return lines;
      } else {
        line = next;
      }
    }
    if (line) lines.push(line);
    if (maxLines > 0 && lines.length >= maxLines) return lines;
  }
  return lines;
}

export class BoardRenderer {
  constructor(canvas) {
    this.canvas = canvas;
    this.ctx = canvas.getContext('2d');
    this.rc = rough.canvas(canvas);
    this.dpr = 1;
    this._imageCache = new Map();
    this._onImageLoaded = null;
  }

  setOnImageLoaded(fn) {
    this._onImageLoaded = fn;
  }

  preloadImage(fileId, url) {
    if (!fileId || !url) return;
    const key = String(fileId);
    const existing = this._imageCache.get(key);
    if (existing?.url === url && existing.loaded) return;
    if (existing?.url === url && existing.loading) return;
    const img = new Image();
    const entry = { url, img, loaded: false, loading: true, failed: false };
    this._imageCache.set(key, entry);
    img.onload = () => {
      entry.loaded = true;
      entry.loading = false;
      if (this._onImageLoaded) this._onImageLoaded();
    };
    img.onerror = () => {
      entry.failed = true;
      entry.loading = false;
      if (this._onImageLoaded) this._onImageLoaded();
    };
    img.src = url;
  }

  resize(width, height) {
    this.dpr = window.devicePixelRatio || 1;
    this.canvas.width = Math.floor(width * this.dpr);
    this.canvas.height = Math.floor(height * this.dpr);
    this.canvas.style.width = `${width}px`;
    this.canvas.style.height = `${height}px`;
    this.ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
  }

  render(state) {
    const { width, height } = state.size;
    const pal = BOARD_PALETTE;
    const ctx = this.ctx;
    const rc = this.rc;
    const outlineSeparate = !!state.outlineSeparate;

    ctx.save();
    ctx.clearRect(0, 0, width, height);
    ctx.fillStyle = pal.bg;
    ctx.fillRect(0, 0, width, height);

    ctx.save();
    ctx.translate(state.viewport.panX, state.viewport.panY);
    const zoom = state.viewport.zoom / 100;
    ctx.scale(zoom, zoom);

    if (state.showGrid !== false) {
      this._drawGrid(ctx, pal, state.viewport, width, height, zoom);
    }

    // elements[] 顺序即 Z 序：先画的在下，后画的在上
    for (const el of state.elements || []) {
      this.drawElement(el, state);
    }

    if (!outlineSeparate) {
      if (state.linkPreview) {
        this._drawLinkPreview(rc, state.linkPreview, state.elements, pal);
      }
      if (state.marqueePreview) {
        this._drawMarquee(ctx, state.marqueePreview, pal);
      }
      if (state.guides) {
        this._drawGuides(ctx, state.guides, pal);
      }
      if (!state.exportMode) {
        this._drawSelectionHandles(ctx, state, pal);
      }
    }

    ctx.restore();

    if (!outlineSeparate && state.eraserPreview) {
      this._drawEraserPreview(ctx, state.eraserPreview, pal);
    }

    ctx.restore();
  }

  /**
   * 交互描边层：透明底，画在媒体 DOM 之上。
   * 坐标系与主画布一致（viewport + DPR 由 Board 统一 resize）。
   */
  renderOutline(state) {
    const { width, height } = state.size;
    const pal = BOARD_PALETTE;
    const ctx = this.ctx;
    const rc = this.rc;

    ctx.save();
    ctx.clearRect(0, 0, width, height);

    ctx.save();
    ctx.translate(state.viewport.panX, state.viewport.panY);
    const zoom = state.viewport.zoom / 100;
    ctx.scale(zoom, zoom);

    if (state.linkPreview) {
      this._drawLinkPreview(rc, state.linkPreview, state.elements, pal);
    }
    if (state.marqueePreview) {
      this._drawMarquee(ctx, state.marqueePreview, pal);
    }
    if (state.guides) {
      this._drawGuides(ctx, state.guides, pal);
    }
    this._drawSelectedMediaCardStrokes(ctx, state, pal);
    if (!state.exportMode) {
      this._drawSelectionHandles(ctx, state, pal);
    }

    ctx.restore();

    if (state.eraserPreview) {
      this._drawEraserPreview(ctx, state.eraserPreview, pal);
    }

    ctx.restore();
  }

  /** 文件/网页卡选中描边（主画布在 outlineSeparate 时不再画选中色） */
  _drawSelectedMediaCardStrokes(ctx, state, pal) {
    const selectedIds = state.selectedIds || [];
    if (!selectedIds.length) return;
    const selected = new Set(selectedIds);
    ctx.save();
    ctx.strokeStyle = pal.cardSelected || '#6965db';
    ctx.lineWidth = 2;
    for (const el of state.elements || []) {
      if (!el || el.type !== 'card' || !selected.has(el.id)) continue;
      const isFile = el.cardType === 'file' && el.fileId;
      const isWebpage = el.cardType === 'webpage' && (el.pageUrl || el.link);
      if (!isFile && !isWebpage) continue;
      ctx.strokeRect(el.x + 0.75, el.y + 0.75, el.width - 1.5, el.height - 1.5);
    }
    ctx.restore();
  }

  /** 绘制单个元素（世界坐标；调用方已设置 viewport 变换） */
  drawElement(el, state = {}) {
    if (!el?.type) return;
    const pal = BOARD_PALETTE;
    const ctx = this.ctx;
    const rc = this.rc;
    const selectedIds = state.selectedIds || [];
    if (el.type === 'frame') this._drawFrame(rc, ctx, el, pal, selectedIds, state.editingFrameId);
    else if (el.type === 'freedraw') this._drawFreedraw(ctx, el, pal);
    else if (el.type === 'rectangle') this._drawRectangle(rc, el, pal, selectedIds);
    else if (el.type === 'ellipse') this._drawEllipse(rc, el, pal, selectedIds);
    else if (el.type === 'text') this._drawText(ctx, el, pal, state.editingTextId);
    else if (el.type === 'arrow') this._drawArrow(rc, ctx, el, pal);
    else if (el.type === 'card') {
      this._drawCard(rc, ctx, el, pal, selectedIds, state.imageUrls, state);
    }
  }

  _drawEraserPreview(ctx, preview, pal) {
    const { x, y, r } = preview;
    ctx.save();
    ctx.strokeStyle = pal.cardSelected || '#6965db';
    ctx.fillStyle = 'rgba(105, 101, 219, 0.14)';
    ctx.lineWidth = 1.5;
    ctx.setLineDash([4, 3]);
    ctx.beginPath();
    ctx.arc(x, y, r, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.restore();
  }

  _drawGrid(ctx, pal, viewport, viewW, viewH, zoom) {
    const step = GRID_STEP;
    const startX = Math.floor(-viewport.panX / zoom / step) * step - step;
    const startY = Math.floor(-viewport.panY / zoom / step) * step - step;
    const endX = startX + viewW / zoom + step * 2;
    const endY = startY + viewH / zoom + step * 2;

    ctx.strokeStyle = pal.grid;
    ctx.lineWidth = 1;
    ctx.beginPath();
    for (let x = startX; x <= endX; x += step) {
      ctx.moveTo(x, startY);
      ctx.lineTo(x, endY);
    }
    for (let y = startY; y <= endY; y += step) {
      ctx.moveTo(startX, y);
      ctx.lineTo(endX, y);
    }
    ctx.stroke();

    ctx.fillStyle = pal.gridDot;
    for (let x = startX; x <= endX; x += step) {
      for (let y = startY; y <= endY; y += step) {
        ctx.beginPath();
        ctx.arc(x, y, 0.65, 0, Math.PI * 2);
        ctx.fill();
      }
    }
  }

  _drawGuides(ctx, guides, pal) {
    ctx.strokeStyle = pal.guide;
    ctx.lineWidth = 1;
    ctx.setLineDash([4, 4]);
    ctx.globalAlpha = 0.75;
    for (const x of guides.v || []) {
      ctx.beginPath();
      ctx.moveTo(x, -1e4);
      ctx.lineTo(x, 1e4);
      ctx.stroke();
    }
    for (const y of guides.h || []) {
      ctx.beginPath();
      ctx.moveTo(-1e4, y);
      ctx.lineTo(1e4, y);
      ctx.stroke();
    }
    ctx.setLineDash([]);
    ctx.globalAlpha = 1;
  }

  _drawFreedraw(ctx, el, pal) {
    const pts = el.points || [];
    if (pts.length < 2) return;
    const d = freedrawPath(pts, el.strokeWidth || 2);
    if (!d) return;
    ctx.fillStyle = el.strokeColor || pal.stroke;
    ctx.fill(new Path2D(d));
  }

  _drawRectangle(rc, el, pal, selectedIds) {
    const selected = selectedIds.includes(el.id);
    rc.rectangle(el.x, el.y, el.width, el.height, {
      stroke: selected ? pal.cardSelected : el.strokeColor || pal.stroke,
      fill: el.fillColor || pal.shapeFill,
      fillStyle: 'solid',
      roughness: 1.1,
      strokeWidth: selected ? 2.2 : 2,
    });
  }

  _drawEllipse(rc, el, pal, selectedIds) {
    const selected = selectedIds.includes(el.id);
    const cx = el.x + el.width / 2;
    const cy = el.y + el.height / 2;
    rc.ellipse(cx, cy, el.width, el.height, {
      stroke: selected ? pal.cardSelected : el.strokeColor || pal.stroke,
      fill: el.fillColor || pal.shapeFill,
      fillStyle: 'solid',
      roughness: 1.1,
      strokeWidth: selected ? 2.2 : 2,
    });
  }

  _drawFrame(rc, ctx, el, pal, selectedIds, editingFrameId) {
    const selected = selectedIds.includes(el.id);
    rc.rectangle(el.x, el.y, el.width, el.height, {
      stroke: selected ? pal.cardSelected : el.strokeColor || pal.muted,
      fill: el.fillColor || 'rgba(148, 163, 184, 0.08)',
      fillStyle: 'solid',
      roughness: 0.8,
      strokeWidth: selected ? 2 : 1.5,
      strokeLineDash: [8, 6],
    });
    if (el.id === editingFrameId) return;
    const label = (el.label || 'Frame').slice(0, 32);
    ctx.fillStyle = pal.muted;
    ctx.font = '600 11px "DM Sans", "PingFang SC", sans-serif';
    ctx.fillText(label, el.x + 8, el.y - 6);
  }

  _drawLinkPreview(rc, preview, elements, pal) {
    const source = preview.source || {
      type: 'card',
      id: preview.sourceId,
    };
    const target = preview.target || {
      type: 'card',
      id: preview.targetId,
    };
    const src = resolveEndpoint(source, elements);
    const tgt = resolveEndpoint(target, elements);
    if (!src || !tgt) return;
    const tc = endpointCenter(tgt);
    const sc = endpointCenter(src);
    const a = linkAnchorPoint(src, tc.x, tc.y);
    const b = linkAnchorPoint(tgt, sc.x, sc.y);
    rc.line(a[0], a[1], b[0], b[1], {
      stroke: pal.cardSelected,
      roughness: 1.05,
      strokeWidth: 2,
      strokeLineDash: [6, 4],
    });
  }

  _drawText(ctx, el, pal, editingTextId) {
    if (el.id === editingTextId) return;
    const text = el.text || '';
    if (!text) return;
    if (containsMath(text)) return;
    const fontSize = el.fontSize || 20;
    const color = el.strokeColor || pal.text;
    ctx.fillStyle = color;
    ctx.font = `${fontSize}px ${TEXT_FONT_FAMILY}`;
    const lines = text.split('\n');
    const lineHeight = fontSize * 1.25;
    let y = el.y + fontSize;
    for (const line of lines) {
      ctx.fillText(line, el.x + 2, y, el.width ? el.width - 4 : undefined);
      y += lineHeight;
    }
  }

  _drawArrow(rc, ctx, el, pal) {
    const pts = el.points;
    if (!pts || pts.length < 2) return;
    const [a, b] = pts;
    const meta = el.connectionType ? connectionMeta(el.connectionType) : null;
    const stroke = el.strokeColor || meta?.color || pal.stroke;
    let labelText = '';
    if (el.connectionLabel != null) {
      labelText = String(el.connectionLabel).trim();
    } else if (el.connectionType !== 'blank' && meta?.label) {
      labelText = meta.label;
    }
    rc.line(a[0], a[1], b[0], b[1], {
      stroke,
      roughness: 1.05,
      strokeWidth: 2,
    });
    const angle = Math.atan2(b[1] - a[1], b[0] - a[0]);
    const head = 11;
    const x1 = b[0] - head * Math.cos(angle - 0.35);
    const y1 = b[1] - head * Math.sin(angle - 0.35);
    const x2 = b[0] - head * Math.cos(angle + 0.35);
    const y2 = b[1] - head * Math.sin(angle + 0.35);
    rc.line(b[0], b[1], x1, y1, { stroke, roughness: 1 });
    rc.line(b[0], b[1], x2, y2, { stroke, roughness: 1 });
    if (labelText) {
      const mx = (a[0] + b[0]) / 2;
      const my = (a[1] + b[1]) / 2;
      ctx.fillStyle = stroke;
      ctx.font = '11px "DM Sans", "PingFang SC", sans-serif';
      ctx.fillText(labelText, mx - 12, my - 8);
    }
  }

  _drawMediaCardFallback(ctx, el, x, y, w, h, isFile) {
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(x, y, w, h);
    ctx.fillStyle = '#94a3b8';
    ctx.font = '12px "DM Sans", "PingFang SC", sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(
      isFile ? '文件预览未写入' : '跨域预览未写入（可授权标签页捕获）',
      x + w / 2,
      y + h / 2,
      Math.max(8, w - 24),
    );
    ctx.textAlign = 'left';
    ctx.textBaseline = 'alphabetic';
  }

  _drawCard(rc, ctx, el, pal, selectedIds, imageUrls, state = {}) {
    const selected = selectedIds.includes(el.id);
    const isImage = el.cardType === 'image' && el.fileId;
    const isFile = el.cardType === 'file' && el.fileId;
    const isWebpage = el.cardType === 'webpage' && (el.pageUrl || el.link);
    const stroke = selected ? pal.cardSelected : el.strokeColor || el.color || pal.cardStroke;
    const fill = el.fillColor || pal.cardFill;

    if (isFile || isWebpage) {
      ctx.save();
      const outlineSeparate = !!state.outlineSeparate;
      const showSelectedStroke = selected && !outlineSeparate;
      const inset = showSelectedStroke ? 1 : 0.75;
      const lw = showSelectedStroke ? 2 : 1.5;
      const ix = el.x + inset;
      const iy = el.y + inset;
      const iw = Math.max(0, el.width - inset * 2);
      const ih = Math.max(0, el.height - inset * 2);
      if (state.exportMode) {
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(ix, iy, iw, ih);
        const bmp = state.previewBitmaps?.[el.id];
        if (bmp) {
          try {
            ctx.drawImage(bmp, ix, iy, iw, ih);
          } catch (_) {
            this._drawMediaCardFallback(ctx, el, ix, iy, iw, ih, isFile);
          }
        } else {
          this._drawMediaCardFallback(ctx, el, ix, iy, iw, ih, isFile);
        }
      } else {
        ctx.clearRect(ix, iy, iw, ih);
      }
      // 选中描边改由 outline 层绘制，避免被上层媒体 DOM 切断
      if (!outlineSeparate || !selected) {
        ctx.strokeStyle = showSelectedStroke
          ? pal.cardSelected
          : el.strokeColor || el.color || pal.cardStroke;
        ctx.lineWidth = lw;
        ctx.strokeRect(el.x + 0.75, el.y + 0.75, el.width - 1.5, el.height - 1.5);
      }
      ctx.restore();
      return;
    }

    if (isImage) {
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(el.x, el.y, el.width, el.height);

      const url = imageUrls?.[String(el.fileId)];
      if (url) this.preloadImage(el.fileId, url);
      const cached = this._imageCache.get(String(el.fileId));
      const pad = MEDIA_CARD_PAD;
      const inner = mediaCardInnerRect(el);
      ctx.fillStyle = pal.text;
      ctx.font = '600 12px "DM Sans", "PingFang SC", sans-serif';
      ctx.fillText((el.title || '图片').slice(0, 24), el.x + 10, el.y + 16, el.width - 20);
      if (cached?.loaded && cached.img) {
        ctx.save();
        ctx.beginPath();
        ctx.rect(inner.x, inner.y, inner.w, inner.h);
        ctx.clip();
        ctx.drawImage(cached.img, inner.x, inner.y, inner.w, inner.h);
        ctx.restore();
      } else {
        ctx.fillStyle = pal.muted;
        ctx.font = '11px "DM Sans", "PingFang SC", sans-serif';
        ctx.fillText(cached?.failed ? '图片加载失败' : '加载中…', inner.x + 8, inner.y + inner.h / 2);
      }

      ctx.strokeStyle = stroke;
      ctx.lineWidth = selected ? 2 : 1.5;
      ctx.strokeRect(el.x + 0.75, el.y + 0.75, el.width - 1.5, el.height - 1.5);
      return;
    }

    rc.rectangle(el.x, el.y, el.width, el.height, {
      stroke,
      fill,
      fillStyle: 'solid',
      roughness: 1.08,
      strokeWidth: selected ? 2.2 : 1.5,
    });

    ctx.fillStyle = pal.text;
    ctx.font = '600 13px "DM Sans", "PingFang SC", sans-serif';
    const title = (el.title || '').trim();
    if (title) {
      ctx.fillText(title.slice(0, 40), el.x + 12, el.y + 22, el.width - 24);
    }
    ctx.fillStyle = pal.muted;
    ctx.font = '12px "DM Sans", "PingFang SC", sans-serif';
    const body = (el.body || '').trim();
    if (body && !containsMath(body)) {
      const padX = 12;
      const bodyTop = title ? el.y + 38 : el.y + 26;
      const bodyBottom = el.y + el.height - 10;
      const lineHeight = 12 * 1.25;
      const maxLines = Math.max(1, Math.floor((bodyBottom - bodyTop) / lineHeight));
      const maxWidth = Math.max(24, el.width - padX * 2);
      const allLines = wrapTextLines(ctx, body, maxWidth, 0);
      const clipped = allLines.slice(0, maxLines);
      if (allLines.length > maxLines && clipped.length) {
        let last = clipped[clipped.length - 1];
        while (last.length > 1 && ctx.measureText(`${last}…`).width > maxWidth) {
          last = last.slice(0, -1);
        }
        clipped[clipped.length - 1] = `${last}…`;
      }
      let y = bodyTop + lineHeight;
      for (const line of clipped) {
        ctx.fillText(line, el.x + padX, y, maxWidth);
        y += lineHeight;
      }
    }
  }

  _drawMarquee(ctx, marquee, pal) {
    const { x, y, w, h, mode } = marquee;
    if (w < 1 || h < 1) return;
    ctx.save();
    const stroke = mode === 'group' ? pal.cardSelected || '#6965db' : pal.muted;
    ctx.strokeStyle = stroke;
    ctx.fillStyle =
      mode === 'group' ? 'rgba(105, 101, 219, 0.08)' : 'rgba(134, 142, 150, 0.12)';
    ctx.lineWidth = 1.5;
    ctx.setLineDash([6, 4]);
    ctx.fillRect(x, y, w, h);
    ctx.strokeRect(x, y, w, h);
    ctx.setLineDash([]);
    ctx.restore();
  }

  _drawSelectionHandles(ctx, state, pal) {
    const strokeSelectionBounds = (el) => {
      const b = boundsOf(el);
      ctx.strokeRect(b.left, b.top, b.right - b.left, b.bottom - b.top);
    };

    if (state.selectedIds.length > 1) {
      ctx.save();
      ctx.strokeStyle = pal.cardSelected || '#6965db';
      ctx.lineWidth = 1.5;
      ctx.setLineDash([4, 3]);
      for (const id of state.selectedIds) {
        const el = state.elements.find((e) => e.id === id);
        if (!el) continue;
        strokeSelectionBounds(el);
      }
      ctx.setLineDash([]);
      ctx.restore();
      return;
    }
    if (state.selectedIds.length !== 1) return;
    const el = state.elements.find((e) => e.id === state.selectedIds[0]);
    if (!el) return;

    if (el.type === 'text' && el.id !== state.editingTextId) {
      ctx.save();
      ctx.strokeStyle = pal.cardSelected || '#6965db';
      ctx.lineWidth = 1.5;
      ctx.setLineDash([4, 3]);
      strokeSelectionBounds(el);
      ctx.setLineDash([]);
      ctx.restore();
      return;
    }

    if (!isResizableElement(el)) return;
    const zoom = Math.max(state.viewport.zoom / 100, 0.1);
    const b = boundsOf(el);
    const corners = [
      [b.left, b.top],
      [b.right, b.top],
      [b.left, b.bottom],
      [b.right, b.bottom],
    ];
    const r = 7 / zoom;
    ctx.fillStyle = '#ffffff';
    ctx.strokeStyle = pal.cardSelected || '#6965db';
    ctx.lineWidth = 1.5 / zoom;
    for (const [cx, cy] of corners) {
      ctx.beginPath();
      ctx.rect(cx - r, cy - r, r * 2, r * 2);
      ctx.fill();
      ctx.stroke();
    }
  }
}
